/**
 * Kanban column model for the chemistry-check board. Columns answer "whose
 * court is the ball in?" rather than mirroring the raw case-state enum, so a
 * coordinator sees at a glance what needs them, who's responded, and where a
 * hand-off is due. Pure + unit-tested; `columnFor` mirrors the server's
 * case-state derivation so the board never disagrees with reality.
 */

import type { ChemistryCase } from '../types';

export type ChemistryColumnKey =
  | 'needs_you'
  | 'waiting_coach'
  | 'waiting_client'
  | 'ready_to_schedule'
  | 'scheduled'
  | 'confirm';

export interface ChemistryColumn {
  key: ChemistryColumnKey;
  label: string;
  hint: string;
  /** Left-accent + header colour. */
  accent: string;
}

export const CHEMISTRY_COLUMNS: ChemistryColumn[] = [
  { key: 'needs_you', label: 'Needs you', hint: 'escalations · selections · overdue', accent: 'var(--warn)' },
  { key: 'waiting_coach', label: 'Waiting on coach', hint: 'chasing coach availability', accent: 'var(--navy)' },
  { key: 'waiting_client', label: 'Waiting on client', hint: 'client picking from times', accent: 'var(--navy)' },
  { key: 'ready_to_schedule', label: 'Ready to schedule', hint: 'create the Outlook invite', accent: '#5e35b1' },
  { key: 'scheduled', label: 'Scheduled', hint: 'invite out · meeting pending', accent: '#2e7d32' },
  { key: 'confirm', label: 'Confirm it happened', hint: 'mark done / no-show', accent: '#2d6a9e' },
];

/**
 * Which column a case belongs in (exactly one). Priority mirrors the backend's
 * `deriveCaseMeta`: coordinator-blocking states first, then the most-advanced
 * actionable track. Closed cases should be filtered out before this is called.
 */
export function columnFor(c: ChemistryCase): ChemistryColumnKey {
  if (c.state === 'needs_human' || c.escalationReason || c.state === 'awaiting_selection') return 'needs_you';
  if (c.state === 'awaiting_completion') return 'confirm';
  if (c.state === 'scheduled') return 'scheduled';
  if (c.tracks.some((t) => t.state === 'ready_to_schedule')) return 'ready_to_schedule';
  if (c.tracks.some((t) => t.state === 'waiting_client')) return 'waiting_client';
  if (c.tracks.some((t) => t.state === 'waiting_coach')) return 'waiting_coach';
  return 'waiting_coach';
}

/** Bucket cases into columns, preserving input order within each column. */
export function bucketByColumn(cases: ChemistryCase[]): Record<ChemistryColumnKey, ChemistryCase[]> {
  const out = {
    needs_you: [],
    waiting_coach: [],
    waiting_client: [],
    ready_to_schedule: [],
    scheduled: [],
    confirm: [],
  } as Record<ChemistryColumnKey, ChemistryCase[]>;
  for (const c of cases) out[columnFor(c)].push(c);
  return out;
}
